import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Dimensions,
  Image,
  Share,
  Linking,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
// import { useCrossPortalSync } from '../core/CrossPortalIntegration';
// import { useAutomatedWorkflow } from '../core/AutomatedHiringWorkflow';
import { useAuth } from '../../../context/AuthContext';
import { useDatabase } from '../../../context/DatabaseContext';
import { 
  useUsers, 
  useCompanies, 
  useNotifications,
  useDashboard
} from '../../../hooks/useDatabaseHooks';
import axios from 'axios';
import { useFocusEffect } from '@react-navigation/native';
const { width, height } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; const bid = await AsyncStorage.getItem('company_id'); if (bid) c.params = { ...c.params, company_id: parseInt(bid) }; } catch {} return c; }, e => Promise.reject(e));
const EP = { jobs: API_BASE_URL + '/api/data/jobs', applications: API_BASE_URL + '/api/data/applications', companyProfile: API_BASE_URL + '/api/data/company_profiles', applicantCounts: API_BASE_URL + '/api/recruitment/applicant-counts', postJob: API_BASE_URL + '/api/recruitment/jobs/post' };
// Bare /api/data/* endpoints return arrays directly; some wrapped routes return
// { success, data: [...] }. Handle every shape we see in the wild so the rest
// of the screen never falls back to mock data when the real API is reachable.
const safeData = r => {
  if (!r) return [];
  const body = r.data ?? r;          // axios wraps in .data; some places we get raw
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.rows)) return body.rows;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.jobs)) return body.jobs;
  if (Array.isArray(body?.applications)) return body.applications;
  return [];
};
// CrossPortalSync and AutomatedWorkflow are used inside component


export default function PostJobScreen({ navigation, route }) {
  const [jobs, setJobs] = useState([]);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('Your Company');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [jobApplicants, setJobApplicants] = useState({});
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplicants: 0,
    totalViews: 0,
    conversionRate: 0,
  });

  const tabs = [
    { id: 'active', name: 'Active', icon: 'briefcase-check' },
    { id: 'draft', name: 'Draft', icon: 'file-document-outline' },
    { id: 'paused', name: 'Paused', icon: 'pause-circle' },
    { id: 'closed', name: 'Closed', icon: 'briefcase-remove' },
    { id: 'all', name: 'All Jobs', icon: 'briefcase' },
  ];

  const quickActions = [
    {
      id: 'create',
      title: 'Create New Job',
      subtitle: 'Post a new job opening',
      icon: 'plus-circle',
      color: '#10B981',
      action: () => navigation.navigate('CreateJobScreen', { companyLogo, companyName }),
    },
    {
      id: 'manage',
      title: 'Manage Jobs',
      subtitle: 'Bulk operations & filters',
      icon: 'cog',
      color: '#8B5CF6',
      action: () => navigation.navigate('JobManagementScreen'),
    },
    {
      id: 'analytics',
      title: 'View Analytics',
      subtitle: 'Performance insights',
      icon: 'chart-line',
      color: '#F59E0B',
      action: () => navigation.navigate('JobPostAnalytics'),
    },
    {
      id: 'templates',
      title: 'Job Templates',
      subtitle: 'Reusable job templates',
      icon: 'file-multiple',
      color: '#EF4444',
      action: () => navigation.navigate('JobTemplates'),
    },
  ];

  const renderQuickActions = () => (
    <View style={styles.quickActionsContainer}>
      <Text style={styles.quickActionsTitle}>Quick Actions</Text>
      <FlatList
        data={quickActions}
        numColumns={2}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={styles.quickActionCard}
            onPress={item.action}
          >
            <View style={[styles.quickActionIcon, { backgroundColor: item.color }]}>
              <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.quickActionTitle}>{item.title}</Text>
            <Text style={styles.quickActionSubtitle}>{item.subtitle}</Text>
          </TouchableOpacity>
        )}
        columnWrapperStyle={styles.quickActionRow}
        scrollEnabled={false}
      />
    </View>
  );

  // Reload jobs + applicant counts every time screen is focused (e.g. coming back from pipeline)
  useFocusEffect(useCallback(() => {
    loadJobs(); // loadAnalytics is called at end of loadJobs
    loadCompanyProfile();
  }, []));
  const loadCompanyProfile = async () => {
    try {
      // AsyncStorage first
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) { const p = JSON.parse(cached); setCompanyLogo(p.logo || null); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); }
      // API
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        setCompanyId(cid);
        try {
          const r = await api.get(EP.companyProfile, { params: { company_id: cid } });
          if (r.data?.success && r.data.data?.length > 0) {
            const lp = r.data.data[0];
            setCompanyLogo(lp.logo || null);
            if (lp.name || lp.company_name) setCompanyName(lp.name || lp.company_name);
          }
        } catch {}
      }
    } catch {}
  };;


  const loadJobs = async () => {
    setIsLoading(true);
    try {
      // Try API first — NEVER default to '1' (would leak company 1 data)
      const cid = companyId || await AsyncStorage.getItem('company_id') || '';
      if (!cid) { setIsLoading(false); return; } // guard: no company → no jobs
      const [jobsRes, appsRes] = await Promise.allSettled([
        api.get(EP.jobs, { params: { company_id: parseInt(cid), limit: 100 } }).catch(() => null),
        api.get(EP.applications, { params: { company_id: parseInt(cid), limit: 500 } }).catch(() => null),
      ]);
      const apiJobs = safeData(jobsRes.status === 'fulfilled' ? jobsRes.value : null);
      const apiApps = safeData(appsRes.status === 'fulfilled' ? appsRes.value : null);

      // Count applicants per job — check ALL possible job ID fields
      const appCounts = {};
      apiApps.forEach(app => {
        const jid = app.job_id || app.job_id || app.jobId || app.job;
        if (jid) {
          const key = String(jid);
          appCounts[key] = (appCounts[key] || 0) + 1;
        }
      });
      setJobApplicants(appCounts);
      console.log('📊 Jobs loaded:', apiJobs.length, '| Applications loaded:', apiApps.length);
      console.log('📊 Applicant counts per job:', JSON.stringify(appCounts));
      if (apiApps.length > 0) console.log('📊 Sample app fields:', Object.keys(apiApps[0]).join(', '));

      if (apiJobs.length > 0) {
        const mapped = apiJobs.map(j => ({
          id: j.id,
          job_id: j.job_id || String(j.id),
          title: j.title || 'Untitled',
          department: j.department || 'General',
          location: j.location || 'Remote',
          employmentType: j.employment_type || j.job_type || 'Full-time',
          jobType: j.job_type || j.employment_type || 'Full-time',
          experienceLevel: j.experience_level || '',
          status: (j.status || 'draft').toLowerCase(),
          isActive: j.is_active !== false,
          salaryMin: parseInt(j.salary_min) || 0,
          salaryMax: parseInt(j.salary_max) || 0,
          salaryCurrency: j.salary_currency || 'GBP',
          createdAt: j.created_at || j.posted_date || new Date().toISOString(),
          updatedAt: j.updated_at || new Date().toISOString(),
          postedDate: j.posted_date || j.published_at || j.created_at || '',
          applicationDeadline: j.application_deadline || '',
          description: j.description || j.short_description || '',
          requirements: Array.isArray(j.qualifications) ? j.qualifications.join('\n') : (j.qualifications || ''),
          responsibilities: Array.isArray(j.responsibilities) ? j.responsibilities.join('\n') : (j.responsibilities || ''),
          requiredSkills: j.required_skills || j.skills || [],
          preferredSkills: j.preferred_skills || [],
          isRemote: (j.location || '').toLowerCase().includes('remote'),
          isUrgent: j.is_urgent || j.urgency === 'urgent' || false,
          isFeatured: j.is_featured || false,
          priority: j.priority || 'medium',
          positionsAvailable: j.positions_available || 1,
          applicants: appCounts[String(j.id)] || parseInt(j.applications_count) || 0,
          views: parseInt(j.views_count) || 0,
          shortlistedCount: parseInt(j.shortlisted_count) || 0,
          interviewCount: parseInt(j.interview_count) || 0,
          offerCount: parseInt(j.offer_count) || 0,
          hiredCount: parseInt(j.hired_count) || 0,
          conversionRate: parseInt(j.views_count) > 0 ? Math.round(((appCounts[String(j.id)] || 0) / parseInt(j.views_count)) * 100) : 0,
        }));
        setJobs(mapped);
        await AsyncStorage.setItem('jobs', JSON.stringify(mapped));
        // Calculate analytics from fresh data immediately
        const totalJobs = mapped.length;
        const activeJobs = mapped.filter(job => (job.status || '').toLowerCase() === 'active').length;
        const totalApplicants = mapped.reduce((sum, job) => sum + (job.applicants || 0), 0);
        const totalViews = mapped.reduce((sum, job) => sum + (job.views || 0), 0);
        const conversionRate = totalViews > 0 ? Math.round((totalApplicants / totalViews) * 100) : 0;
        setAnalytics({ totalJobs, activeJobs, totalApplicants, totalViews, conversionRate });
        console.log('📊 Analytics: jobs=' + totalJobs + ' active=' + activeJobs + ' applicants=' + totalApplicants);
      } else {
        // No jobs from API — show empty state. Never substitute mock data;
        // that hid real "you have no jobs yet" state behind fake records and
        // made the screen look misleadingly populated.
        setJobs([]);
        setAnalytics({ totalJobs: 0, activeJobs: 0, totalApplicants: 0, totalViews: 0, conversionRate: 0 });
        await AsyncStorage.removeItem('jobs').catch(() => {});
      }
    } catch (error) {
      console.warn('loadJobs:', error?.message);
      // Last-resort: load cached jobs from AsyncStorage if the network failed.
      // Still no mock data — empty is the honest answer if cache is also empty.
      try {
        const s = await AsyncStorage.getItem('jobs');
        if (s) setJobs(JSON.parse(s));
        else setJobs([]);
      } catch { setJobs([]); }
    } finally { setIsLoading(false); }
  };

  const loadAnalytics = async () => {
    try {
      // Use current jobs state first, fallback to cache
      const jobsData = jobs.length > 0 ? jobs : JSON.parse(await AsyncStorage.getItem('jobs') || '[]');
      if (jobsData.length > 0) {
        const totalJobs = jobsData.length;
        const activeJobs = jobsData.filter(job => (job.status || '').toLowerCase() === 'active').length;
        const totalApplicants = jobsData.reduce((sum, job) => sum + (job.applicants || 0), 0);
        const totalViews = jobsData.reduce((sum, job) => sum + (job.views || 0), 0);
        const conversionRate = totalViews > 0 ? Math.round((totalApplicants / totalViews) * 100) : 0;

        setAnalytics({
          totalJobs,
          activeJobs,
          totalApplicants,
          totalViews,
          conversionRate,
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const generateMockJobs = () => {
    const jobTitles = [
      'Senior Software Engineer', 'Product Manager', 'UX Designer', 'Data Scientist',
      'DevOps Engineer', 'Marketing Manager', 'Sales Representative', 'Business Analyst',
      'Frontend Developer', 'Backend Developer', 'Mobile Developer', 'QA Engineer'
    ];
    
    const departments = ['Engineering', 'Product', 'Design', 'Data Science', 'Marketing', 'Sales'];
    const locations = ['London', 'Manchester', 'Birmingham', 'Remote', 'Hybrid - London'];
    const statuses = ['active', 'draft', 'paused', 'closed'];
    const employmentTypes = ['full-time', 'part-time', 'contract', 'freelance'];

    return Array.from({ length: 12 }, (_, index) => ({
      id: `job_${index + 1}`,
      title: jobTitles[index % jobTitles.length],
      department: departments[Math.floor(Math.random() * departments.length)],
      location: locations[Math.floor(Math.random() * locations.length)],
      employmentType: employmentTypes[Math.floor(Math.random() * employmentTypes.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      salaryMin: Math.floor(Math.random() * 30000) + 30000,
      salaryMax: Math.floor(Math.random() * 50000) + 60000,
      createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      applicationDeadline: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      description: `We are looking for a talented ${jobTitles[index % jobTitles.length]} to join our growing team...`,
      requirements: 'Bachelor\'s degree in relevant field, 3+ years experience...',
      responsibilities: 'Lead development projects, collaborate with cross-functional teams...',
      isRemote: Math.random() > 0.5,
      isUrgent: Math.random() > 0.8,
      isFeatured: Math.random() > 0.7,
      aiPowered: Math.random() > 0.3,
      applicants: Math.floor(Math.random() * 50) + 5,
      views: Math.floor(Math.random() * 200) + 20,
      conversionRate: Math.floor(Math.random() * 15) + 5,
    }));
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadJobs();
    await loadAnalytics();
    setRefreshing(false);
  };

  const filteredJobs = jobs.filter(job => {
    const matchesTab = selectedTab === 'all' || job.status === selectedTab;
    const matchesSearch = String(job.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(job.department ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(job.location ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getTabCount = (tabId) => {
    if (tabId === 'all') return jobs.length;
    return jobs.filter(job => job.status === tabId).length;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'draft': return '#F59E0B';
      case 'paused': return '#6B7280';
      case 'closed': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleJobPress = (job) => {
    navigation.navigate('PostJobDetailsScreen', { 
      job: { ...job, applicantCount: job.applicants || jobApplicants[String(job.id)] || 0 }, 
      companyLogo, companyName,
      jobId: job.id || job.job_id,
      jobTitle: job.title,
    });
  };

  const handleViewApplicants = (job) => {
    const count = job.applicants || jobApplicants[String(job.id)] || 0;
    navigation.navigate('ApplicantsPipelineScreen', { 
      jobId: job.id || job.job_id,
      jobTitle: job.title,
      applicantCount: count,
      companyLogo, companyName,
      filterByJob: true,
      jobData: {
        id: job.id || job.job_id,
        job_id: job.job_id || job.id,
        title: job.title,
        department: job.department,
        type: job.employmentType || job.jobType || job.type || 'Full-time',
        location: job.location,
        salaryMin: job.salaryMin,
        salaryMax: job.salaryMax,
        status: job.status || 'draft',
        description: job.description,
      }
    });
  };

  const handleJobAction = (job, action) => {
    switch (action) {
      case 'edit':
        navigation.navigate('CreateJobScreen', { job, mode: 'edit', companyLogo, companyName });
        break;
      case 'duplicate':
        navigation.navigate('CreateJobScreen', { job, mode: 'duplicate', companyLogo, companyName });
        break;
      case 'pause':
        updateJobStatus(job.id, 'paused');
        break;
      case 'activate':
        updateJobStatus(job.id, 'active');
        break;
      case 'share':
        shareJob(job);
        break;
      case 'close':
        updateJobStatus(job.id, 'closed');
        break;
      case 'delete':
        deleteJob(job.id);
        break;
      default:
        break;
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const updatedJobs = jobs.map(job => 
        job.id === jobId ? { ...job, status: newStatus, updatedAt: new Date().toISOString() } : job
      );
      setJobs(updatedJobs);
      await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));
      await syncJobToApi(updatedJobs.find(j => j.id === jobId), 'PUT');
      await loadAnalytics();
      Alert.alert('Success', `Job ${newStatus} successfully!`);
    } catch (error) {
      console.error('Error updating job status:', error);
      Alert.alert('Error', 'Failed to update job status. Please try again.');
    }
  };

  const deleteJob = async (jobId) => {
    Alert.alert(
      'Delete Job',
      'Are you sure you want to delete this job? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Delete from API
              try {
                const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
                await fetch(`https://api.jobeng.ai/api/data/jobs/${jobId}`, {
                  method: 'DELETE',
                  headers: { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                });
              } catch (e) { console.warn('API delete failed:', e.message); }
              const updatedJobs = jobs.filter(job => job.id !== jobId);
              setJobs(updatedJobs);
              await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));
              await loadAnalytics();
              Alert.alert('Success', 'Job deleted successfully!');
            } catch (error) {
              console.error('Error deleting job:', error);
              Alert.alert('Error', 'Failed to delete job. Please try again.');
            }
          }
        }
      ]
    );
  };


  // Share job to social media
  const shareJob = async (job) => {
    const message = `🚀 We're Hiring: ${job.title}\n🏢 ${companyName}\n📍 ${job.location || 'Remote'}\n💰 ${job.salaryMin ? formatCurrency(job.salaryMin) + ' - ' + formatCurrency(job.salaryMax) : 'Competitive'}\n\nApply: https://jobeng.app/jobs/${job.id}`;
    try {
      await Share.share({ message, title: `Job: ${job.title}` });
      // Track share — skip if no company in session
      const cid = await AsyncStorage.getItem('company_id') || '';
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      fetch(`https://api.jobeng.ai/api/data/job_share_analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job_id: job.id, platform: 'native_share', company_id: parseInt(cid), shared_at: new Date().toISOString() }),
      }).catch(() => {});
    } catch (e) { console.error('Error sharing:', e); }
  };

  // Quick share to LinkedIn
  const shareJobLinkedIn = async (job) => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://jobeng.app/jobs/${job.id}`)}`;
    try { await Linking.openURL(url); } catch (e) { Alert.alert('Error', 'Could not open LinkedIn'); }
  };

  // Quick share to WhatsApp
  const shareJobWhatsApp = async (job) => {
    const msg = `Check out this job: ${job.title} at ${companyName}. Apply: https://jobeng.app/jobs/${job.id}`;
    try { await Linking.openURL(`whatsapp://send?text=${encodeURIComponent(msg)}`); } catch (e) { Alert.alert('Error', 'Could not open WhatsApp'); }
  };

  // API sync for job CRUD — delegates to shared module which enforces
  // is_active=true + posted_date so the job appears in professional search
  const syncJobToApi = async (job, method = 'POST') => {
    try {
      const { saveJobPosting } = require('./jobsShared');
      const status = (job.status || 'active').toLowerCase();
      const isClosing = status === 'closed' || status === 'draft' || status === 'paused';
      await saveJobPosting(
        {
          id: job.id,
          title: job.title,
          department: job.department,
          location: job.location,
          jobType: job.jobType || job.employmentType || 'Full-time',
          employmentType: job.employmentType || job.jobType || 'Full-time',
          experienceLevel: job.experienceLevel || '',
          salaryMin: job.salaryMin || 0,
          salaryMax: job.salaryMax || 0,
          salaryCurrency: job.salaryCurrency || 'GBP',
          description: job.description || '',
          requiredSkills: job.requiredSkills || [],
          positionsAvailable: job.positionsAvailable || 1,
          isFeatured: job.isFeatured || false,
          applicationDeadline: job.applicationDeadline,
        },
        { mode: method === 'POST' ? 'create' : 'update' }
      );
      // If the job is being closed/drafted after save, flip is_active off
      if (isClosing && job.id) {
        const { setJobActive } = require('./jobsShared');
        await setJobActive(job.id, false);
      }
    } catch (e) { console.warn('API job sync failed:', e.message); }
  };


  const renderAnalyticsOverview = () => (
  <View style={styles.analyticsContainer}>
    <Text style={styles.analyticsTitle}>Job Posting Overview</Text>
    <FlatList
      data={[
        {
          id: 'totalJobs',
          icon: 'briefcase',
          color: '#8B5CF6',
          backgroundColor: '#EEF2FF',
          value: analytics.totalJobs,
          label: 'Total Jobs'
        },
        {
          id: 'activeJobs', 
          icon: 'briefcase-check',
          color: '#10B981',
          backgroundColor: '#ECFDF5',
          value: analytics.activeJobs,
          label: 'Active'
        },
        {
          id: 'applicants',
          icon: 'account-group', 
          color: '#F59E0B',
          backgroundColor: '#FEF3C7',
          value: analytics.totalApplicants,
          label: 'Applicants'
        },
        {
          id: 'conversion',
          icon: 'percent',
          color: '#0EA5E9', 
          backgroundColor: '#F0F9FF',
          value: `${analytics.conversionRate}%`,
          label: 'Conversion'
        }
      ]}
      numColumns={2}
      renderItem={({ item }) => (
        <View style={[styles.analyticsCard, { backgroundColor: item.backgroundColor }]}>
          <MaterialCommunityIcons name={item.icon} size={24} color={item.color} />
          <Text style={styles.analyticsValue}>{item.value}</Text>
          <Text style={styles.analyticsLabel}>{item.label}</Text>
        </View>
      )}
      columnWrapperStyle={styles.analyticsRow}
      scrollEnabled={false}
      keyExtractor={(item) => item.id}
    />
    <TouchableOpacity
      style={styles.viewAnalyticsButton}
      onPress={() => navigation.navigate('JobPostAnalytics')}
    >
      <Text style={styles.viewAnalyticsText}>View Detailed Analytics</Text>
      <MaterialCommunityIcons name="arrow-right" size={16} color="#8B5CF6" />
    </TouchableOpacity>
  </View>
  );

  const renderJobCard = ({ item: job }) => (
    <TouchableOpacity
      style={styles.jobCard}
      onPress={() => handleJobPress(job)}
      activeOpacity={0.7}
    >
      {/* Job Header */}
      <View style={styles.jobHeader}>
        <View style={styles.jobTitleContainer}>
          <Text style={styles.jobTitle}>{job.title}</Text>
          <View style={styles.jobBadges}>
            {job.isFeatured && (
              <View style={styles.featuredBadge}>
                <MaterialCommunityIcons name="star" size={12} color="#FFFFFF" />
                <Text style={styles.featuredText}>Featured</Text>
              </View>
            )}
            {job.aiPowered && (
              <View style={styles.aiPoweredBadge}>
                <MaterialCommunityIcons name="robot" size={12} color="#FFFFFF" />
                <Text style={styles.aiPoweredText}>AI Powered</Text>
              </View>
            )}
            {job.isUrgent && (
              <View style={styles.urgentBadge}>
                <MaterialCommunityIcons name="clock-fast" size={12} color="#FFFFFF" />
                <Text style={styles.urgentText}>Urgent</Text>
              </View>
            )}
          </View>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(job.status) }]}>
          <Text style={styles.statusText}>{(job.status || 'draft').toUpperCase()}</Text>
        </View>
      </View>

      {/* Job Info */}
      <View style={styles.jobInfo}>
        <View style={styles.jobInfoRow}>
          <View style={styles.jobInfoItem}>
            <MaterialCommunityIcons name="domain" size={16} color="#6B7280" />
            <Text style={styles.jobInfoText}>{job.department}</Text>
          </View>
          <View style={styles.jobInfoItem}>
            <MaterialCommunityIcons name="map-marker" size={16} color="#6B7280" />
            <Text style={styles.jobInfoText}>{job.location}</Text>
          </View>
        </View>
        <View style={styles.jobInfoRow}>
          <View style={styles.jobInfoItem}>
            <MaterialCommunityIcons name="clock" size={16} color="#6B7280" />
            <Text style={styles.jobInfoText}>{job.employmentType}</Text>
          </View>
          <View style={styles.jobInfoItem}>
            <MaterialCommunityIcons name="currency-gbp" size={16} color="#6B7280" />
            <Text style={styles.jobInfoText}>
              {formatCurrency(job.salaryMin)} - {formatCurrency(job.salaryMax)}
            </Text>
          </View>
        </View>
      </View>

      {/* Job Stats */}
      <View style={styles.jobStats}>
        <TouchableOpacity style={styles.jobStat} onPress={() => handleViewApplicants(job)}>
          <MaterialCommunityIcons name="account-group" size={18} color="#3B82F6" />
          <Text style={styles.jobStatValue}>{job.applicants || jobApplicants[String(job.id)] || 0}</Text>
          <Text style={[styles.jobStatLabel, { color: '#3B82F6' }]}>Applicants</Text>
        </TouchableOpacity>
        <View style={styles.jobStat}>
          <MaterialCommunityIcons name="eye" size={18} color="#10B981" />
          <Text style={styles.jobStatValue}>{job.views}</Text>
          <Text style={styles.jobStatLabel}>Views</Text>
        </View>
        <View style={styles.jobStat}>
          <MaterialCommunityIcons name="calendar" size={18} color="#F59E0B" />
          <Text style={styles.jobStatValue}>{getDaysRemaining(job.applicationDeadline)}</Text>
          <Text style={styles.jobStatLabel}>Days Left</Text>
        </View>
        <View style={styles.jobStat}>
          <MaterialCommunityIcons name="chart-line" size={18} color="#8B5CF6" />
          <Text style={styles.jobStatValue}>{job.conversionRate}%</Text>
          <Text style={styles.jobStatLabel}>Conversion</Text>
        </View>
      </View>

      {/* Job Actions */}
      <View style={styles.jobActions}>
        <TouchableOpacity
          style={styles.primaryActionButton}
          onPress={() => handleViewApplicants(job)}
        >
          <MaterialCommunityIcons name="account-group" size={16} color="#FFFFFF" />
          <Text style={styles.primaryActionText}>View Applicants</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.secondaryActionButton}
          onPress={() => handleJobAction(job, 'edit')}
        >
          <MaterialCommunityIcons name="pencil" size={16} color="#6B7280" />
          <Text style={styles.secondaryActionText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => {
            // Show action sheet with more options
            Alert.alert(
              'Job Actions',
              'Choose an action',
              [
                { text: 'Duplicate', onPress: () => handleJobAction(job, 'duplicate') },
                { text: job.status === 'active' ? 'Pause' : 'Activate', onPress: () => handleJobAction(job, job.status === 'active' ? 'pause' : 'activate') },
                { text: 'Close', onPress: () => handleJobAction(job, 'close') },
                { text: 'Delete', style: 'destructive', onPress: () => handleJobAction(job, 'delete') },
                { text: 'Cancel', style: 'cancel' }
              ]
            );
          }}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color="#6B7280" />
        </TouchableOpacity>
      </View>

      {/* Job Footer */}
      <View style={styles.jobFooter}>
        <Text style={styles.jobFooterText}>
          Posted {formatDate(job.createdAt)} • Deadline {formatDate(job.applicationDeadline)}
        </Text>
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.fullScreenContainer}>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <SafeAreaView style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#FFFFFF" />
          <Text style={styles.loadingText}>Loading job postings...</Text>
        </SafeAreaView>
      </LinearGradient>
    );
  }

  return (
    <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.fullScreenContainer}>
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
      <SafeAreaView style={styles.safeContainer}>
        
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <TouchableOpacity
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
            </TouchableOpacity>
            
            <View style={styles.headerInfo}>
              <View style={styles.headerTitleContainer}>
                {/* Company logo synced from CompanyProfileScreen */}
                <TouchableOpacity
                  onPress={() => navigation.navigate('CompanyProfileScreen')}
                  style={{ marginRight: 10 }}>
                  {companyLogo ? (
                    <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' }} />
                  ) : (
                    <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
                  )}
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Job Posting</Text>
              </View>
              <Text style={styles.headerSubtitle}>
                {companyName} • {analytics.activeJobs} Active Jobs
              </Text>
            </View>
            
            <TouchableOpacity
              style={styles.createButton}
              onPress={() => navigation.navigate('CreateJobScreen', { companyLogo, companyName })}
            >
              <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
            </TouchableOpacity>
          </View>

          {/* Search Bar */}
          <View style={styles.searchContainer}>
            <View style={styles.searchBar}>
              <MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
              <TextInput
                style={styles.searchInput}
                placeholder="Search jobs..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholderTextColor="#9CA3AF"
              />
              {searchQuery ? (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <MaterialCommunityIcons name="close" size={20} color="#9CA3AF" />
                </TouchableOpacity>
              ) : null}
            </View>
            <TouchableOpacity
              style={styles.filterButton}
              onPress={() => navigation.navigate('JobManagementScreen')}
            >
              <MaterialCommunityIcons name="filter-variant" size={20} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Content Container with rounded top corners */}
        <View style={styles.contentContainer}>
          {/* Tabs */}
          <View style={styles.tabsContainer}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.tabsContent}
            >
              {tabs.map((tab) => (
                <TouchableOpacity
                  key={tab.id}
                  style={[styles.tab, selectedTab === tab.id && styles.activeTab]}
                  onPress={() => setSelectedTab(tab.id)}
                >
                  <MaterialCommunityIcons 
                    name={tab.icon} 
                    size={16} 
                    color={selectedTab === tab.id ? '#3B82F6' : '#6B7280'} 
                  />
                  <Text style={[styles.tabText, selectedTab === tab.id && styles.activeTabText]}>
                    {tab.name}
                  </Text>
                  <View style={[styles.tabBadge, selectedTab === tab.id && styles.activeTabBadge]}>
                    <Text style={[styles.tabBadgeText, selectedTab === tab.id && styles.activeTabBadgeText]}>
                      {getTabCount(tab.id)}
                    </Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
          >
            {/* Analytics Overview */}
            {renderAnalyticsOverview()}

            {/* Quick Actions */}
            {renderQuickActions()}

            {/* Jobs List */}
            <View style={styles.jobsSection}>
              <View style={styles.jobsSectionHeader}>
                <Text style={styles.jobsSectionTitle}>
                  {selectedTab === 'all' ? 'All Jobs' : `${selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)} Jobs`}
                </Text>
                <Text style={styles.jobsCount}>
                  <Text>{filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'}</Text>
                </Text>
              </View>

              {filteredJobs.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="briefcase-outline" size={64} color="#D1D5DB" />
                  <Text style={styles.emptyTitle}>No jobs found</Text>
                  <Text style={styles.emptySubtitle}>
                    {searchQuery 
                      ? 'Try adjusting your search terms'
                      : selectedTab === 'all'
                      ? 'Create your first job posting to get started'
                      : `No ${selectedTab} jobs available`
                    }
                  </Text>
                  {!searchQuery && selectedTab === 'all' && (
                    <TouchableOpacity
                      style={styles.createJobButton}
                      onPress={() => navigation.navigate('CreateJobScreen', { companyLogo, companyName })}
                    >
                      <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
                      <Text style={styles.createJobButtonText}>Create Job</Text>
                    </TouchableOpacity>
                  )}
                </View>
              ) : (
                <FlatList
                  data={filteredJobs}
                  renderItem={renderJobCard}
                  keyExtractor={(item) => item.id}
                  scrollEnabled={false}
                  showsVerticalScrollIndicator={false}
                />
              )}
            </View>
          </ScrollView>

          {/* Floating Action Button */}
          <TouchableOpacity
            style={styles.fab}
            onPress={() => navigation.navigate('CreateJobScreen', { companyLogo, companyName })}
          >
            <MaterialCommunityIcons name="plus" size={28} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
  },
  safeContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#FFFFFF',
    marginTop: 12,
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  createButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#FFFFFF',
    marginLeft: 8,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    overflow: 'hidden',
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#3B82F6',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    marginLeft: 6,
    marginRight: 8,
  },
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  tabBadge: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  activeTabBadge: {
    backgroundColor: '#3B82F6',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
  },
  analyticsContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    borderRadius: 16,
    padding: 20,
  },
  analyticsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 16,
  },
  analyticsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
  },
  analyticsCard: {
  flex: 1,
  padding: 16,
  borderRadius: 12,
  alignItems: 'center',
  margin: 6,
  maxWidth: (width - 72) / 2, // Ensures exactly 2 columns
},

analyticsRow: {
  justifyContent: 'space-between',
  paddingHorizontal: 8,
},

  analyticsValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 8,
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  viewAnalyticsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#8B5CF6',
    borderRadius: 12,
  },
  viewAnalyticsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8B5CF6',
    marginRight: 8,
  },
  quickActionsContainer: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 20,
  },
  quickActionsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 16,
  },
  quickActionRow: {
    justifyContent: 'space-between',
  },
  quickActionCard: {
    width: (width - 72) / 2,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    margin: 6,
  },
  quickActionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  quickActionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    textAlign: 'center',
    marginBottom: 4,
  },
  quickActionSubtitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  jobsSection: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 20,
  },
  jobsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  jobsSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  jobsCount: {
    fontSize: 14,
    color: '#6B7280',
  },
  jobCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  jobHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  jobTitleContainer: {
    flex: 1,
    marginRight: 12,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 8,
  },
  jobBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  featuredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F59E0B',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 4,
  },
  featuredText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  aiPoweredBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 4,
  },
  aiPoweredText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  urgentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EF4444',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 4,
  },
  urgentText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  jobInfo: {
    marginBottom: 12,
  },
  jobInfoRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  jobInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  jobInfoText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 6,
  },
  jobStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  jobStat: {
    alignItems: 'center',
    flex: 1,
  },
  jobStatValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 4,
  },
  jobStatLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  jobActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  primaryActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginRight: 8,
  },
  primaryActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 6,
  },
  secondaryActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginRight: 8,
  },
  secondaryActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    marginLeft: 6,
  },
  moreButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  jobFooter: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
  },
  jobFooterText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  createJobButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
  },
  createJobButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#3B82F6',
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
});